import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';

import { getProductBySlug, fetchProductsByCategory, updateProduct, deleteProduct } from '../lib/products';
import { getFileUrl } from '../lib/pb';
import { fetchCategories } from '../lib/categories';
import { pb } from '../lib/pb';
import { useFavorites } from '../hooks/useFavorites';

export default function ProductDetail() {
  const { slug, productSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isFavorite, toggle } = useFavorites();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [slugToCat, setSlugToCat] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', description: '', price: '', published: true,
    category: '', extra_categories: [], imagesExisting: [], imagesNew: [], techSheetFile: null
  });
  const [allCategories, setAllCategories] = useState([]);
  const [draggingImgIdx, setDraggingImgIdx] = useState(null);
  const [hoverThumbIdx, setHoverThumbIdx] = useState(null);
  const [confirmDelIdx, setConfirmDelIdx] = useState(null);

  useEffect(() => {
    const effectiveSlug = productSlug || slug;
    if (!effectiveSlug) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const item = await getProductBySlug(effectiveSlug);
        if (!cancelled) {
          setProduct(item);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Produit introuvable');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug, productSlug]);

  // Load categories when viewing under /catalogue to resolve slugs to names for breadcrumb
  useEffect(() => {
    if (!location.pathname.startsWith('/catalogue')) {
      setSlugToCat({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchCategories('catalogue');
        if (cancelled) return;
        const map = {};
        for (const c of cats) { if (c.slug) map[c.slug] = c; }
        setSlugToCat(map);
      } catch {
        if (!cancelled) setSlugToCat({});
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Load categories list for admin selects
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchCategories('catalogue');
        if (cancelled) return;
        setAllCategories(cats || []);
      } catch {/* no-op */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!product || !product.category) {
      setSimilar([]);
      return;
    }
    let cancelled = false;
    async function loadSimilar() {
      try {
        const list = await fetchProductsByCategory(product.category);
        if (cancelled) return;
        const filtered = (list || []).filter(p => p.id !== product.id && p.slug);
        setSimilar(filtered.slice(0, 4));
      } catch (e) {
        if (!cancelled) {
          setSimilar([]);
        }
      }
    }
    loadSimilar();
    return () => { cancelled = true; };
  }, [product]);

  const isAdminSession = (() => {
    try { return !!pb?.authStore?.isValid; } catch { return false; }
  })();

  useEffect(() => {
    if (!product) return;
    setForm({
      title: product.title || '',
      slug: product.slug || '',
      excerpt: product.excerpt || '',
      description: product.description || '',
      price: product.price ?? '',
      published: !!product.published,
      category: product.category || product.expand?.category?.id || '',
      extra_categories: Array.isArray(product.extra_categories) ? product.extra_categories : (Array.isArray(product.expand?.extra_categories) ? product.expand.extra_categories.map(x => x.id).filter(Boolean) : []),
      imagesExisting: Array.isArray(product.images) ? [...product.images] : [],
      imagesNew: [],
      techSheetFile: null,
    });
    setEditMode(false);
  }, [product]);

  async function handleInlineSave() {
    if (!product) return;
    setSaving(true);
    try {
      const hasFiles = (form.imagesNew && form.imagesNew.length) || !!form.techSheetFile || true; // we always send images order
      let payload;
      if (hasFiles) {
        payload = new FormData();
        payload.append('title', form.title || '');
        payload.append('slug', form.slug || '');
        payload.append('excerpt', form.excerpt || '');
        payload.append('description', form.description || '');
        payload.append('published', form.published ? 'true' : 'false');
        const num = Number(form.price);
        if (form.price === '' || form.price === null || isNaN(num)) {
          payload.append('price', '');
        } else {
          payload.append('price', String(num));
        }
        if (form.category) { payload.append('category', form.category); }
        // extra categories (PocketBase expects repeated keys or JSON, using repeated keys)
        (form.extra_categories || []).forEach(id => payload.append('extra_categories', id));
        // Preserve order: first existing filenames (kept), then newly added files
        (form.imagesExisting || []).forEach(name => payload.append('images', name));
        (form.imagesNew || []).forEach(file => payload.append('images', file));
        if (form.techSheetFile) { payload.append('tech_sheet', form.techSheetFile); }
      } else {
        payload = {
          title: form.title,
          slug: form.slug,
          excerpt: form.excerpt,
          description: form.description,
          published: !!form.published,
          category: form.category || null,
          extra_categories: form.extra_categories || [],
          // For JSON flow without files, PB also accepts array of filenames to keep
          images: form.imagesExisting || [],
        };
        const num = Number(form.price);
        payload.price = (form.price === '' || form.price === null || isNaN(num)) ? null : num;
      }
      const updated = await updateProduct(product.id, payload);
      setProduct(prev => ({ ...prev, ...updated }));
      setEditMode(false);
    } catch (e) {
      alert(e?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  }

  const topRef = useRef(null);

  return (
    <>
    <article className="product-detail-signature">
      <div className="container-about-premium">
        
        {/* BREADCRUMBS */}
        <nav className="breadcrumb-nav-signature animated-up">
          <ol>
            <li><Link to="/">Accueil</Link></li>
            <li className="sep">/</li>
            <li><Link to="/catalogue">Catalogue</Link></li>
            {(() => {
              const segments = location.pathname.split('/').filter(Boolean);
              const idx = segments.indexOf('catalogue');
              const cats = idx >= 0 ? segments.slice(idx + 1).filter(s => s !== (product?.slug || '')) : [];
              return cats.map(s => (
                <div key={s} style={{display:'contents'}}>
                  <li className="sep">/</li>
                  <li><Link to={`/catalogue/${s}`}>{slugToCat[s]?.name || s}</Link></li>
                </div>
              ));
            })()}
            {product && (
              <>
                <li className="sep">/</li>
                <li className="active">{product.title}</li>
              </>
            )}
          </ol>
        </nav>

        {loading && <div className="loader-signature">Chargement de l&apos;excellence...</div>}
        {error && <div className="error-box-signature">{error}</div>}

        {!loading && !error && product && (
          <div className="product-layout-signature" ref={topRef}>
            
            {/* LEFT: GALLERY */}
            <aside className="product-gallery-side animated-up">
              <div className="main-visual-frame shadow-signature">
                {product && (
                  <button
                    type="button"
                    className={`btn-fav-jewel ${isFavorite(product.id) ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(product);
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill={isFavorite(product.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                       <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                    </svg>
                  </button>
                )}

                {isAdminSession && editMode ? (
                  <div className="edit-gallery-container">
                    {(() => {
                        const all = [...(form.imagesExisting || []), ...((form.imagesNew || []).map(f => URL.createObjectURL(f)))];
                        const src = all[activeImageIndex] || all[0];
                        return src ? <img src={typeof src==='string' && src.startsWith('blob:') ? src : getFileUrl(product, src)} alt="Edition" /> : <div className="placeholder">Aucune image</div>;
                    })()}
                  </div>
                ) : (
                   <div className="view-gallery-container" onClick={() => setLightboxOpen(true)}>
                      <img src={getFileUrl(product, product.images[activeImageIndex] || product.images[0])} alt={product.title} />
                      <div className="zoom-hint">Cliquer pour agrandir</div>
                   </div>
                )}
              </div>

              {/* THUMBNAILS */}
              <div className="thumbnails-row-signature">
                 {(isAdminSession && editMode ? [...(form.imagesExisting || []), ...(form.imagesNew || [])] : (product.images || [])).map((img, idx) => {
                    const src = (isAdminSession && editMode && typeof img !== 'string') ? URL.createObjectURL(img) : getFileUrl(product, img);
                    return (
                      <div key={idx} className="thumb-wrapper">
                         <button 
                           className={`thumb-btn ${idx === activeImageIndex ? 'active' : ''}`}
                           onClick={() => setActiveImageIndex(idx)}
                         >
                            <img src={src} alt="thumbnail" />
                         </button>
                         {isAdminSession && editMode && (
                           <button className="btn-del-thumb" onClick={() => setConfirmDelIdx(idx)}>×</button>
                         )}
                      </div>
                    );
                 })}
                 {isAdminSession && editMode && (
                   <label className="btn-add-thumb">
                      <span>+</span>
                      <input type="file" hidden multiple accept="image/*" onChange={(e) => setForm(f=>({...f, imagesNew:[...f.imagesNew, ...Array.from(e.target.files)]}))} />
                   </label>
                 )}
              </div>
            </aside>

            {/* RIGHT: INFO */}
            <main className="product-info-side animated-up" style={{animationDelay: '0.1s'}}>
               {isAdminSession && editMode ? (
                 <div className="admin-edit-card shadow-signature">
                    <h3>Édition du produit</h3>
                    <div className="form-grid">
                       <label>Titre <input value={form.title} onChange={e=>setForm(f=>({...f, title:e.target.value}))} /></label>
                       <label>Slug <input value={form.slug} onChange={e=>setForm(f=>({...f, slug:e.target.value}))} /></label>
                       <label>Catégorie <select value={form.category} onChange={e=>setForm(f=>({...f, category:e.target.value}))}>
                          {allCategories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                       </select></label>
                       <label>Prix (€) <input type="number" value={form.price} onChange={e=>setForm(f=>({...f, price:e.target.value}))} /></label>
                    </div>
                    <label>Accroche <textarea rows={2} value={form.excerpt} onChange={e=>setForm(f=>({...f, excerpt:e.target.value}))} /></label>
                    <label>Description complète <textarea rows={8} value={form.description} onChange={e=>setForm(f=>({...f, description:e.target.value}))} /></label>
                    
                    <div className="admin-actions-row">
                       <button className="btn-save-emerald" onClick={handleInlineSave} disabled={saving}>{saving ? 'Enregistrement...' : 'Sauvegarder'}</button>
                       <button className="btn-cancel-link" onClick={() => setEditMode(false)}>Annuler</button>
                    </div>
                 </div>
               ) : (
                 <div className="product-content-box">
                    <div className="product-meta-top">
                       <span className="cat-badge-premium">{product.expand?.category?.name || 'Innovation AEF32'}</span>
                    </div>
                    <h1 className="product-main-title">{product.title}</h1>
                    {product.excerpt && <p className="product-main-excerpt">{product.excerpt}</p>}
                    
                    <div className="accent-separator"></div>
                    
                    <div className="product-full-description">
                       {product.description || "Aucune description détaillée disponible pour le moment."}
                    </div>

                    {product.tech_sheet && (
                      <a href={getFileUrl(product, product.tech_sheet)} target="_blank" rel="noreferrer" className="tech-sheet-card">
                         <div className="icon">📂</div>
                         <div className="text">
                            <strong>Fiche Technique</strong>
                            <span>Télécharger le format PDF</span>
                         </div>
                         <div className="arrow">↓</div>
                      </a>
                    )}

                    <div className="product-actions-group">
                       <button className="btn-premium-emerald w-full" onClick={() => navigate('/contact')}>
                          Demander une étude personnalisée
                       </button>
                       <div className="action-extras">
                          <button className="btn-link-pro" onClick={() => navigate('/catalogue')}>← Retour au catalogue</button>
                          {isAdminSession && (
                            <button className="btn-edit-small" onClick={() => setEditMode(true)}>Éditer la fiche</button>
                          )}
                       </div>
                    </div>
                 </div>
               )}
            </main>
          </div>
        )}

        {/* SIMILAR PRODUCTS */}
        {!loading && !error && product && similar.length > 0 && (
          <section className="similar-products-section animated-up" style={{animationDelay: '0.2s'}}>
             <div className="section-title-box centered-titles">
                <div className="accent-bar-horiz"></div>
                <h2>Modèles similaires</h2>
             </div>
             <div className="similar-grid-signature">
                {similar.map((p, idx) => (
                  <article key={p.id} className="similar-card shadow-signature" onClick={() => navigate(`/produits/${p.slug}`)}>
                     <div className="similar-visual">
                        {p.images?.[0] ? <img src={getFileUrl(p, p.images[0])} alt={p.title} /> : <div className="placeholder">📸</div>}
                     </div>
                     <div className="similar-info">
                        <h4>{p.title}</h4>
                        <p>{p.excerpt}</p>
                     </div>
                  </article>
                ))}
             </div>
          </section>
        )}

        {/* LIGHTBOX */}
        {product && lightboxOpen && (
          <div className="lightbox-overlay-signature" onClick={() => setLightboxOpen(false)}>
             <div className="lightbox-content" onClick={e=>e.stopPropagation()}>
                <img src={getFileUrl(product, product.images[activeImageIndex] || product.images[0])} alt="Lightbox" />
                <button className="btn-close-lightbox" onClick={() => setLightboxOpen(false)}>×</button>
             </div>
          </div>
        )}

        <style>{`
          .product-detail-signature { background: #fff; min-height: 100vh; padding: 40px 0; font-family: 'Inter', sans-serif; }
          .container-about-premium { max-width: 1200px; margin: 0 auto; padding: 0 20px; }

          /* BREADCRUMB */
          .breadcrumb-nav-signature { margin-bottom: 40px; }
          .breadcrumb-nav-signature ol { display: flex; align-items: center; gap: 12px; list-style: none; padding: 0; margin: 0; font-size: 13px; font-weight: 700; color: #94a3b8; }
          .breadcrumb-nav-signature a { color: #64748b; text-decoration: none; transition: color 0.2s; }
          .breadcrumb-nav-signature a:hover { color: var(--accent); }
          .breadcrumb-nav-signature .sep { opacity: 0.4; }
          .breadcrumb-nav-signature .active { color: var(--accent); }

          /* LAYOUT */
          .product-layout-signature { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 60px; align-items: flex-start; }
          
          /* GALLERY */
          .main-visual-frame { position: relative; border-radius: 32px; overflow: hidden; background: #f8fafc; aspect-ratio: 4/3; border: 1px solid #f1f5f9; }
          .main-visual-frame img { width: 100%; height: 100%; object-fit: cover; }
          .view-gallery-container { cursor: zoom-in; height: 100%; }
          .zoom-hint { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); background: rgba(15,23,42,0.6); color: #fff; padding: 6px 15px; border-radius: 99px; font-size: 11px; font-weight: 800; opacity: 0; transition: opacity 0.3s; }
          .view-gallery-container:hover .zoom-hint { opacity: 1; }

          .btn-fav-jewel { 
             position: absolute; top: 25px; right: 25px; width: 44px; height: 44px; 
             border-radius: 50%; border: none; background: #fff; color: #ef4444; 
             display: flex; align-items: center; justify-content: center; 
             box-shadow: 0 10px 25px rgba(239, 68, 68, 0.15); cursor: pointer; transition: all 0.3s; z-index: 10;
          }
          .btn-fav-jewel:hover { transform: scale(1.1); box-shadow: 0 15px 30px rgba(239, 68, 68, 0.25); }
          .btn-fav-jewel.active { background: #ef4444; color: #fff; }

          .thumbnails-row-signature { display: flex; gap: 15px; margin-top: 20px; flex-wrap: wrap; }
          .thumb-wrapper { position: relative; }
          .thumb-btn { width: 80px; height: 80px; border-radius: 16px; border: 2px solid transparent; overflow: hidden; padding: 0; cursor: pointer; transition: all 0.2s; background: #f1f5f9; }
          .thumb-btn.active { border-color: var(--accent); transform: scale(1.05); }
          .thumb-btn img { width: 100%; height: 100%; object-fit: cover; }
          
          .btn-del-thumb { position: absolute; -top: 5px; -right: 5px; width: 22px; height: 22px; border-radius: 50%; background: #ef4444; color: #fff; border: none; display: flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; }

          /* INFO SIDE */
          .product-meta-top { margin-bottom: 20px; }
          .cat-badge-premium { background: #f0fdf4; color: var(--accent); font-size: 11px; font-weight: 800; padding: 6px 15px; border-radius: 99px; border: 1px solid rgba(34,197,94,0.1); text-transform: uppercase; letter-spacing: 1px; }
          .product-main-title { font-size: clamp(24px, 4vw, 36px); font-weight: 900; margin: 0 0 15px 0; color: #0f172a; letter-spacing: -1px; }
          .product-main-excerpt { font-size: 18px; color: #64748b; line-height: 1.6; margin-bottom: 25px; }
          .accent-separator { width: 60px; height: 4px; background: var(--accent); border-radius: 2px; margin-bottom: 30px; }
          .product-full-description { font-size: 15px; color: #475569; line-height: 1.8; margin-bottom: 40px; white-space: pre-line; }

          .tech-sheet-card { 
             display: flex; align-items: center; gap: 20px; background: #f8fafc; 
             padding: 20px 25px; border-radius: 24px; text-decoration: none; border: 1px solid #f1f5f9;
             transition: all 0.3s; margin-bottom: 40px;
          }
          .tech-sheet-card:hover { border-color: var(--accent); transform: translateX(5px); background: #fff; box-shadow: 0 10px 30px rgba(15,23,42,0.05); }
          .tech-sheet-card .icon { font-size: 24px; }
          .tech-sheet-card .text { flex-grow: 1; }
          .tech-sheet-card .text strong { display: block; font-size: 15px; color: #0f172a; margin-bottom: 2px; }
          .tech-sheet-card .text span { font-size: 12px; color: #94a3b8; font-weight: 700; }
          .tech-sheet-card .arrow { font-size: 18px; color: var(--accent); font-weight: 900; }

          .btn-premium-emerald { 
             background: var(--accent); color: #fff; border: none; padding: 20px 40px; 
             border-radius: 18px; font-size: 16px; font-weight: 800; cursor: pointer;
             transition: all 0.3s; box-shadow: 0 15px 30px rgba(34, 197, 94, 0.2); width: 100%;
          }
          .btn-premium-emerald:hover { transform: translateY(-3px); box-shadow: 0 20px 40px rgba(34, 197, 94, 0.3); }
          
          .action-extras { display: flex; justify-content: space-between; align-items: center; margin-top: 25px; }
          .btn-link-pro { background: none; border: none; font-size: 13px; font-weight: 800; color: #64748b; cursor: pointer; transition: color 0.2s; }
          .btn-link-pro:hover { color: var(--accent); }
          .btn-edit-small { background: #f1f5f9; border: none; padding: 8px 15px; border-radius: 10px; font-size: 11px; font-weight: 800; color: #475569; cursor: pointer; }

          /* ADMIN */
          .admin-edit-card { background: #f0fdf4; padding: 30px; border-radius: 32px; border: 1px solid rgba(34,197,94,0.1); }
          .admin-edit-card h3 { margin: 0 0 25px 0; font-size: 18px; border-bottom: 1px solid rgba(34,197,94,0.1); padding-bottom: 15px; }
          .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .admin-edit-card label { display: flex; flex-direction: column; gap: 8px; font-size: 12px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-bottom: 15px; }
          .admin-edit-card input, .admin-edit-card select, .admin-edit-card textarea { 
             padding: 12px 15px; border-radius: 12px; border: 1px solid #e2e8f0; background: #fff; font-family: inherit; font-size: 14px; color: #0f172a; outline: none;
          }
          .admin-edit-card input:focus { border-color: var(--accent); }
          .admin-actions-row { display: flex; gap: 15px; align-items: center; margin-top: 20px; }
          .btn-save-emerald { background: var(--accent); color: #fff; border: none; padding: 12px 25px; border-radius: 12px; font-weight: 800; cursor: pointer; }

          /* SIMILAR GRID */
          .similar-products-section { margin-top: 80px; padding-top: 60px; border-top: 1px solid #f1f5f9; }
          .similar-grid-signature { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 25px; }
          .similar-card { background: #fff; border-radius: 24px; border: 1px solid #f1f5f9; overflow: hidden; cursor: pointer; transition: all 0.3s; }
          .similar-card:hover { transform: translateY(-8px); border-color: var(--accent); }
          .similar-visual { height: 160px; background: #f8fafc; }
          .similar-visual img { width: 100%; height: 100%; object-fit: cover; }
          .similar-info { padding: 15px; }
          .similar-info h4 { margin: 0 0 5px 0; font-size: 15px; font-weight: 900; }
          .similar-info p { margin: 0; font-size: 12px; color: #64748b; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

          /* LIGHTBOX */
          .lightbox-overlay-signature { position: fixed; inset: 0; background: rgba(15,23,42,0.9); backdrop-filter: blur(15px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 40px; }
          .lightbox-content { position: relative; max-width: 90vw; max-height: 90vh; }
          .lightbox-content img { max-width: 100%; max-height: 90vh; border-radius: 24px; object-fit: contain; }
          .btn-close-lightbox { position: absolute; -top: 20px; -right: 20px; width: 44px; height: 44px; border-radius: 50%; background: #fff; color: #0f172a; border: none; font-size: 24px; cursor: pointer; }

          /* ANIMATIONS */
          .animated-up { animation: fadeUp 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards; opacity: 0; }
          @keyframes fadeUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

          @media (max-width: 1024px) {
             .product-layout-signature { grid-template-columns: 1fr; gap: 40px; }
          }
        `}</style>
      </div>
    </article>
    </>
  );
}
